// askSmart-ui/src/pages/MapleCoin/operations/ControlsTab.tsx
//
// Account controls (C1): holds and restrictions.
//   A HOLD restricts part of a customer's cleared (Available) balance. It is
//   not incoming money — that is Pending — and it posts nothing to the ledger.
//   A RESTRICTION refuses new outgoing or new incoming transactions.
// Placing either is one Operations user and takes effect at once (it only
// ever protects). Releasing either is maker/checker: request it here, and a
// DIFFERENT Operations user approves it. The server and database enforce
// every rule; this screen only withholds buttons that would be refused.
import { FormEvent, useCallback, useEffect, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import {
  AccountControls, ActiveControls, ControlReasonCode, Hold, Intervention, OpsApiError, Restriction, RestrictionType,
  getAccountControls, getActiveControls, placeHold, placeRestriction, requestHoldRelease, requestRestrictionRelease,
} from './opsApi';
import { Amount, Busy, Identity, KeyValues, Loading, Panel, Status, Unavailable, dateTime, parseDecimalToMinor } from './shared';
import { InterventionCard, newKey, refusal } from './TransactionsTab';
import { OpsNav } from './MapleCoinOperations';

const REASONS: [ControlReasonCode, string][] = [
  ['FRAUD_REVIEW', 'Fraud review'], ['COMPLIANCE_REVIEW', 'Compliance review'], ['OPERATIONAL', 'Operational'],
];
const REASON_LABEL = Object.fromEntries(REASONS) as Record<ControlReasonCode, string>;
const RESTRICTIONS: [RestrictionType, string][] = [
  ['NO_OUTGOING', 'No outgoing transactions'], ['NO_INCOMING', 'No incoming transactions'],
];
const RESTRICTION_LABEL = Object.fromEntries(RESTRICTIONS) as Record<RestrictionType, string>;
const ACCOUNT_NUMBER = /^[0-9]{10}$/;

const errorText = (err: unknown) =>
  err instanceof OpsApiError && err.status < 500
    ? refusal(err.code, err.detail ?? undefined)
    : 'Operations is temporarily unavailable. Nothing was changed.';

const placedBy = (c: Hold | Restriction) =>
  c.placedBy.type === 'OPERATOR' ? <Identity sub={c.placedBy.id} /> : <span>{c.placedBy.type.toLowerCase()} · <code>{c.placedBy.id}</code></span>;

// ── Institution-wide: what is active right now ──────────────────────────────

function ActiveList({ onOpen }: { onOpen: (accountNumber: string) => void }) {
  const [data, setData] = useState<ActiveControls | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const load = useCallback(() => {
    setState('loading');
    getActiveControls().then((d) => { setData(d); setState('ready'); }).catch(() => setState('error'));
  }, []);
  useEffect(load, [load]);

  if (state === 'loading') return <Loading label="Loading active holds and restrictions" />;
  if (state === 'error' || !data) return <Unavailable onRetry={load} />;
  return (
    <Panel title="Active holds and restrictions">
      <p className="mco-note" data-testid="active-summary">
        {data.activeHolds} active hold{data.activeHolds === 1 ? '' : 's'}
        {Object.entries(data.heldByCurrency).map(([cur, minor]) => (
          <span key={cur}> · <Amount minor={minor ?? '0'} currency={cur as Hold['currency']} /> held</span>
        ))}
        {' '}· {data.activeRestrictions} active restriction{data.activeRestrictions === 1 ? '' : 's'}
      </p>
      {data.holds.length + data.restrictions.length === 0 ? (
        <p className="mco-empty">Nothing is on hold or restricted.</p>
      ) : (
        <div className="mco-table-wrap">
          <table className="mco-table">
            <thead><tr><th>Reference</th><th>Account</th><th>Control</th><th className="mco-r">Amount</th><th>Reason</th><th>Placed</th><th>By</th></tr></thead>
            <tbody>
              {data.holds.map((h) => (
                <tr key={h.reference}>
                  <td><code>{h.reference}</code></td>
                  <td><button type="button" className="mco-link" onClick={() => onOpen(h.accountNumber)}>{h.accountNumber}</button></td>
                  <td>Hold{h.expiresAt ? ` · expires ${dateTime(h.expiresAt)}` : ''}</td>
                  <td className="mco-r"><Amount minor={h.amountMinor} currency={h.currency} /></td>
                  <td>{REASON_LABEL[h.reasonCode]}</td>
                  <td>{dateTime(h.placedAt)}</td>
                  <td>{placedBy(h)}</td>
                </tr>
              ))}
              {data.restrictions.map((r) => (
                <tr key={r.reference}>
                  <td><code>{r.reference}</code></td>
                  <td><button type="button" className="mco-link" onClick={() => onOpen(r.accountNumber)}>{r.accountNumber}</button></td>
                  <td>{RESTRICTION_LABEL[r.restrictionType]}</td>
                  <td className="mco-r">—</td>
                  <td>{REASON_LABEL[r.reasonCode]}</td>
                  <td>{dateTime(r.placedAt)}</td>
                  <td>{placedBy(r)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

// ── Placing (one Operations user; effective at once) ────────────────────────

function PlaceHold({ account, onPlaced }: { account: AccountControls['account']; onPlaced: () => void }) {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [reasonCode, setReasonCode] = useState<ControlReasonCode>('FRAUD_REVIEW');
  const [reason, setReason] = useState('');
  const [expires, setExpires] = useState('');
  const [key, setKey] = useState(newKey);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const minor = parseDecimalToMinor(amount);
  const valid = minor !== null && minor !== '0' && reason.trim().length > 0;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    setBusy(true);
    setError(null);
    try {
      await placeHold({ accountNumber: account.accountNumber, amountMinor: minor!, currency: account.currency, reasonCode,
        reason: reason.trim(), ...(expires ? { expiresAt: new Date(expires).toISOString() } : {}), idempotencyKey: key });
      setOpen(false);
      setAmount(''); setReason(''); setExpires(''); setKey(newKey());
      onPlaced();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  if (!open) return <button type="button" className="mco-btn" onClick={() => setOpen(true)}>Place hold</button>;
  return (
    <form className="mco-confirm mco-form" aria-label="Place hold" onSubmit={submit}>
      <p>A hold keeps part of the cleared balance from being used. It does not change the balance and posts nothing.
        It takes effect immediately; releasing it needs a second Operations user.</p>
      <label>Amount ({account.currency})
        <input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal" placeholder="0.00" />
      </label>
      <label>Reason code
        <select value={reasonCode} onChange={(e) => setReasonCode(e.target.value as ControlReasonCode)}>
          {REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </label>
      <label>Reason (required)
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} rows={2} />
      </label>
      <label>Expires (optional)
        <input type="datetime-local" value={expires} onChange={(e) => setExpires(e.target.value)} />
      </label>
      {amount && minor === null && <p className="mco-error">Enter an amount like 1,250.00.</p>}
      <div className="mco-actions">
        <button type="submit" className="mco-btn mco-btn--primary" disabled={!valid || busy}><Busy busy={busy}>Place hold</Busy></button>
        <button type="button" className="mco-btn mco-btn--quiet" disabled={busy} onClick={() => setOpen(false)}>Cancel</button>
      </div>
      {error && <p className="mco-error" role="alert">{error}</p>}
    </form>
  );
}

function PlaceRestriction({ account, onPlaced }: { account: AccountControls['account']; onPlaced: () => void }) {
  const [open, setOpen] = useState(false);
  const available = RESTRICTIONS.filter(([t]) => !account.restrictions.includes(t));
  const [type, setType] = useState<RestrictionType>(available[0]?.[0] ?? 'NO_OUTGOING');
  const [reasonCode, setReasonCode] = useState<ControlReasonCode>('COMPLIANCE_REVIEW');
  const [reason, setReason] = useState('');
  const [key, setKey] = useState(newKey);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await placeRestriction({ accountNumber: account.accountNumber, restrictionType: type, reasonCode, reason: reason.trim(), idempotencyKey: key });
      setOpen(false);
      setReason(''); setKey(newKey());
      onPlaced();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  if (available.length === 0) return null;
  if (!open) return <button type="button" className="mco-btn" onClick={() => { setType(available[0][0]); setOpen(true); }}>Place restriction</button>;
  return (
    <form className="mco-confirm mco-form" aria-label="Place restriction" onSubmit={submit}>
      <p>A restriction refuses new transactions of that kind. Money already in flight still completes. It takes effect
        immediately; lifting it needs a second Operations user.</p>
      <label>Restriction
        <select value={type} onChange={(e) => setType(e.target.value as RestrictionType)}>
          {available.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </label>
      <label>Reason code
        <select value={reasonCode} onChange={(e) => setReasonCode(e.target.value as ControlReasonCode)}>
          {REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </label>
      <label>Reason (required)
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} rows={2} />
      </label>
      <div className="mco-actions">
        <button type="submit" className="mco-btn mco-btn--primary" disabled={!reason.trim() || busy}><Busy busy={busy}>Place restriction</Busy></button>
        <button type="button" className="mco-btn mco-btn--quiet" disabled={busy} onClick={() => setOpen(false)}>Cancel</button>
      </div>
      {error && <p className="mco-error" role="alert">{error}</p>}
    </form>
  );
}

// ── Releasing (the maker; a different user decides) ─────────────────────────

function RequestRelease({ reference, kind, onRequested }: { reference: string; kind: 'hold' | 'restriction'; onRequested: () => void }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [key, setKey] = useState(newKey);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await (kind === 'hold' ? requestHoldRelease(reference, reason.trim(), key) : requestRestrictionRelease(reference, reason.trim(), key));
      setOpen(false);
      setReason(''); setKey(newKey());
      onRequested();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  if (!open) return <button type="button" className="mco-btn mco-btn--quiet" onClick={() => setOpen(true)}>Request release</button>;
  return (
    <div className="mco-confirm" role="group" aria-label={`Request release of ${reference}`}>
      <label>Reason for releasing (required)
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} rows={2} />
      </label>
      <p className="mco-note">Nothing changes until a different Operations user approves.</p>
      <div className="mco-actions">
        <button type="button" className="mco-btn" disabled={!reason.trim() || busy} onClick={submit}><Busy busy={busy}>Request release</Busy></button>
        <button type="button" className="mco-btn mco-btn--quiet" disabled={busy} onClick={() => setOpen(false)}>Cancel</button>
      </div>
      {error && <p className="mco-error" role="alert">{error}</p>}
    </div>
  );
}

// ── One account ─────────────────────────────────────────────────────────────

function AccountView({ accountNumber, nav, onBack }: { accountNumber: string; nav: OpsNav; onBack: () => void }) {
  const [data, setData] = useState<AccountControls | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'missing' | 'error'>('loading');
  const load = useCallback(() => {
    getAccountControls(accountNumber)
      .then((d) => { setData(d); setState('ready'); })
      .catch((err) => setState(err instanceof OpsApiError && err.status === 404 ? 'missing' : 'error'));
  }, [accountNumber]);
  useEffect(load, [load]);

  const back = <button type="button" className="mco-back" onClick={onBack}><ArrowLeft size={16} /> All controls</button>;
  if (state === 'loading') return <>{back}<Loading label="Loading account controls" /></>;
  if (state === 'missing') return <>{back}<p className="mco-empty">No account {accountNumber}.</p></>;
  if (state === 'error' || !data) return <>{back}<Unavailable onRetry={load} /></>;

  const a = data.account;
  const openFor = (ref: string) => data.releaseRequests.some((i) => i.status === 'PENDING_APPROVAL'
    && ((i.type === 'HOLD_RELEASE' && i.target.holdReference === ref) || (i.type === 'RESTRICTION_RELEASE' && i.target.restrictionReference === ref)));

  return (
    <>
      {back}
      <Panel title={`Account ${a.accountNumber}`} label="Account balances">
        <KeyValues rows={[
          ['Customer', <code key="customer">{a.customerNumber}</code>],
          ['Account status', <Status key="status" value={a.status} />],
          ['Balance (cleared)', <Amount key="balance" minor={a.availableMinor} currency={a.currency} testId="ctl-balance" />],
          ['On hold', <Amount key="held" minor={a.heldMinor} currency={a.currency} testId="ctl-held" />],
          ['Available to use', <Amount key="spendable" minor={a.spendableMinor} currency={a.currency} testId="ctl-spendable" />],
          ['Pending (incoming, not yet cleared)', <Amount key="pending" minor={a.pendingMinor} currency={a.currency} testId="ctl-pending" />],
          ['Active restrictions', a.restrictions.length ? a.restrictions.map((r) => RESTRICTION_LABEL[r]).join(', ') : 'None'],
        ]} />
        <p className="mco-note">Pending is incoming money that has not cleared; it is never usable and holds never apply to it.</p>
        {a.status !== 'CLOSED' && (
          <div className="mco-actions">
            <PlaceHold account={a} onPlaced={load} />
            <PlaceRestriction account={a} onPlaced={load} />
          </div>
        )}
      </Panel>

      <Panel title="Holds" label="Holds">
        {data.holds.length === 0 ? <p className="mco-empty">No holds on this account.</p> : (
          <div className="mco-table-wrap">
            <table className="mco-table">
              <thead><tr><th>Reference</th><th className="mco-r">Amount</th><th>Reason</th><th>Status</th><th>Placed</th><th>Ended / expires</th><th /></tr></thead>
              <tbody>
                {data.holds.map((h) => (
                  <tr key={h.reference} data-testid={`hold-${h.reference}`}>
                    <td><code>{h.reference}</code></td>
                    <td className="mco-r"><Amount minor={h.amountMinor} currency={h.currency} /></td>
                    <td>{REASON_LABEL[h.reasonCode]}{h.reason ? <div className="mco-dim">{h.reason}</div> : null}</td>
                    <td><Status value={h.status} /></td>
                    <td>{dateTime(h.placedAt)}<div className="mco-dim">{placedBy(h)}</div></td>
                    <td>{h.release ? <>{dateTime(h.release.releasedAt)}<div className="mco-dim">released{h.release.interventionReference ? ` · ${h.release.interventionReference}` : ''}</div></>
                      : h.expiresAt ? dateTime(h.expiresAt) : '—'}</td>
                    <td>{h.status === 'ACTIVE' && (openFor(h.reference)
                      ? <span className="mco-dim">Release awaiting approval</span>
                      : <RequestRelease reference={h.reference} kind="hold" onRequested={load} />)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Panel title="Restrictions" label="Restrictions">
        {data.restrictions.length === 0 ? <p className="mco-empty">No restrictions on this account.</p> : (
          <div className="mco-table-wrap">
            <table className="mco-table">
              <thead><tr><th>Reference</th><th>Restriction</th><th>Reason</th><th>Status</th><th>Placed</th><th>Released</th><th /></tr></thead>
              <tbody>
                {data.restrictions.map((r) => (
                  <tr key={r.reference} data-testid={`restriction-${r.reference}`}>
                    <td><code>{r.reference}</code></td>
                    <td>{RESTRICTION_LABEL[r.restrictionType]}</td>
                    <td>{REASON_LABEL[r.reasonCode]}{r.reason ? <div className="mco-dim">{r.reason}</div> : null}</td>
                    <td><Status value={r.status} /></td>
                    <td>{dateTime(r.placedAt)}<div className="mco-dim">{placedBy(r)}</div></td>
                    <td>{r.release ? dateTime(r.release.releasedAt) : '—'}</td>
                    <td>{r.status === 'ACTIVE' && (openFor(r.reference)
                      ? <span className="mco-dim">Release awaiting approval</span>
                      : <RequestRelease reference={r.reference} kind="restriction" onRequested={load} />)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {data.releaseRequests.length > 0 && (
        <Panel title="Release requests" label="Release requests">
          {data.releaseRequests.map((i: Intervention) => <InterventionCard key={i.reference} i={i} nav={nav} onDecided={load} />)}
        </Panel>
      )}
    </>
  );
}

// ── Tab ─────────────────────────────────────────────────────────────────────

export default function ControlsTab({ nav, initialAccount }: { nav: OpsNav; initialAccount?: string }) {
  const [account, setAccount] = useState<string | null>(initialAccount ?? null);
  const [lookup, setLookup] = useState('');
  const go = (e: FormEvent) => {
    e.preventDefault();
    if (ACCOUNT_NUMBER.test(lookup.trim())) setAccount(lookup.trim());
  };
  if (account) return <AccountView accountNumber={account} nav={nav} onBack={() => setAccount(null)} />;
  return (
    <>
      <form className="mco-panel mco-filters" role="search" aria-label="Open an account's controls" onSubmit={go}>
        <label>Account number
          <input value={lookup} onChange={(e) => setLookup(e.target.value)} placeholder="10 digits" inputMode="numeric" />
        </label>
        <button type="submit" className="mco-btn" disabled={!ACCOUNT_NUMBER.test(lookup.trim())}>Open</button>
      </form>
      <ActiveList onOpen={setAccount} />
    </>
  );
}
